/**
 * feat-300 — every on-screen string for the AI edit thread (design NOTES §4).
 *
 * Same pattern as `QUIZ_REVIEW_NAV_COPY`: on-screen copy renders through these
 * constants, never as literals, so a grep for a sentence finds it here.
 *
 * Level words come from `QUIZ_TERMS` so a rename of "Question" follows.
 *
 * Never show an HTTP status, a server message, a request id or model text in a
 * failure box. The AI's own words appear ONLY as clarifying questions and
 * proposal summaries, rendered as text nodes.
 */
import { QUIZ_TERMS } from '../../quizTerminology';
import type { QuestionEditFailure } from '../../../../services/questionEditService';

const Q = QUIZ_TERMS.question.singular;

const pad2 = (n: number) => (n < 10 ? `0${n}` : String(n));

export const QUESTION_EDIT_COPY = {
  askAi: 'Ask AI',
  askAiAria: (n: number) => `Ask AI to edit ${Q} ${n} by voice`,
  askAiTitle: `Ask AI to edit this ${Q} — speak`,
  typeAria: (n: number) => `Ask AI to edit ${Q} ${n} by typing`,
  typeTitle: 'Type instead',

  chipWorking: 'AI working',
  chipQuestion: 'AI has a question',
  chipProposal: 'Proposal ready',
  chipFailed: 'Failed',
  chipAria: {
    working: 'AI edit: the AI is working on it',
    question: 'AI edit: the AI has a question for you',
    proposal: 'AI edit: a proposed change is ready to review',
    failed: (reason: string) => `AI edit failed: ${reason}`,
  },
  chipToggle: (open: boolean) => (open ? 'Hide the thread.' : 'Show the thread.'),

  panelTitle: 'AI edit',
  panelState: {
    notSent: 'not sent yet',
    recording: 'recording',
    transcribing: 'transcribing',
    working: 'AI working',
    needsReply: 'the AI has a question',
    proposal: 'proposal ready',
    failed: 'failed',
    applied: 'applied',
    discarded: 'discarded',
  },
  hide: 'Hide',
  logLabel: (n: number) => `AI edit thread for ${Q} ${n}`,
  you: 'You',
  ai: 'AI',
  byVoice: 'by voice',
  typed: 'typed',

  placeholderFirst:
    'Say or type what’s wrong, e.g. “Option C is also technically right — make it clearly wrong.”',
  placeholderReply: 'Answer the AI…',
  boxLabelFirst: `What should change in this ${Q}?`,
  boxLabelReply: 'Your reply to the AI',
  speak: 'Speak',
  addByVoice: 'Add by voice',
  send: 'Send',
  sendReply: 'Send reply',
  sendHint: 'sends',

  recording: 'Recording',
  recordingTime: (m: number, s: number) => `${m}:${pad2(s)}`,
  recordingCap: ' / 2:00',
  recordingAria: (m: number, s: number) => `${m} minutes ${s} seconds of 2`,
  secondsLeft: (n: number) => `${n} seconds left`,
  stop: 'Stop',
  cancel: 'Cancel',
  recHint:
    'Stops on its own at 2 minutes. Your recording is turned into text for you to check — the audio isn’t kept.',
  capStopped: 'Stopped at the 2-minute limit.',
  transcribing: 'Turning your recording into text…',
  fromRecording: 'From your recording — fix any misheard words, then send.',
  micDenied:
    'Your browser is blocking the microphone, so type instead. To use your voice, allow the microphone for this site in your browser’s settings.',
  micUnavailable: 'No microphone is available here, so type instead.',
  nothingHeard:
    'We didn’t hear anything in that recording, so nothing was sent. Try again closer to the mic, or type instead.',
  transcribeFailedLead: 'Couldn’t turn that recording into text.',
  transcribeFailedTail: 'Nothing was sent.',
  recordAgain: 'Record again',
  orTypeInstead: 'or type instead.',
  transcribeErr: {
    tooLong:
      'That recording is over 2 minutes, so it wasn’t sent. Record a shorter one, or type instead.',
    unsupported: 'This browser recorded in a format we can’t read. Type instead, or try Chrome.',
  },

  working: 'The AI is working on it.',
  workingLeave: `You can hide this or leave the page — it keeps going, and this ${Q}’s chip will change when it’s done.`,
  aiQuestionSr: 'Question from the AI: ',

  proposalEyebrow: (n: number) => `Proposed change · ${n} ${n === 1 ? 'field' : 'fields'}`,
  noChangeEyebrow: 'No change needed',
  current: 'Current',
  proposed: 'Proposed',
  apply: 'Apply',
  reply: 'Reply',
  discard: 'Discard',
  refineHint: 'Reply to refine it — the new proposal replaces this one.',
  closeThread: 'Close thread',
  nothingToApply: 'Nothing to apply.',
  replacedProposal: (n: number) => `Proposal · ${n} ${n === 1 ? 'field' : 'fields'}`,
  /** The contract keeps only the current proposal's fields, so an earlier one
   *  has no count to show (QUESTIONS-FRONTEND F6). */
  replacedProposalNoCount: 'Proposal',
  replacedSuffix: ' — replaced',
  addedSr: 'added: ',
  removedSr: 'removed: ',

  applyConfirmLead: 'Apply this change?',
  applyConfirmRequired:
    'It becomes an unapproved edit and goes to Approvals. Employees keep seeing the approved version until someone approves it.',
  applyConfirmBtnRequired: 'Apply change',
  applyConfirmOff: 'It goes live on employees’ phones right away — this location doesn’t require approval.',
  applyConfirmBtnOff: 'Apply and go live',
  applyConfirmUnknown: `It changes the ${Q} now. If approval is required here, it waits in Approvals; if not, employees see it right away.`,
  applying: 'Applying…',
  appliedLead: 'Applied.',
  appliedOff: 'It’s live on employees’ phones.',
  discardedLead: 'Discarded.',
  discardedTail: `The ${Q} is unchanged.`,
  staleLead: `This ${Q} was edited since the AI proposed this`,
  staleTail: ' — ask it to redo, or discard. Nothing was changed.',
  askRedo: 'Ask AI to redo',
  redoTurn: `The ${Q} changed since your last proposal — redo it against the current version.`,
  failedLead: 'The AI couldn’t finish this.',
  failedReason: {
    ai_error: 'The AI service didn’t respond. Your message is saved — Retry sends it again.',
    ai_refused: `The AI declined this request. Try rewording it, or edit the ${Q} yourself.`,
    timeout: 'It took longer than 5 minutes, so it was stopped. Retry sends your message again.',
    ai_invalid_output: `Its suggestion broke a rule for this ${Q} (for example, a missing option), so it was dropped. Retry asks again.`,
    unknown: 'Something went wrong. Retry sends your message again.',
  } as Record<QuestionEditFailure | 'unknown', string>,
  retry: 'Retry',

  historyOutcome: { applied: 'Applied', discarded: 'Discarded' },
  historyLine: (outcome: string, date: string, who: string) =>
    `AI edit · ${outcome} · ${date} · ${who}`,
  showMoreHistory: (n: number) => `Show ${n} more`,
  appliedBy: (who: string, when: string) => `Applied by ${who} · ${when}`,
  historyLoading: 'Loading…',

  needsYou: 'need you',
  needsYouAria: (n: number) =>
    `${n} ${n === 1 ? Q : QUIZ_TERMS.question.plural} need${n === 1 ? 's' : ''} you. Jump to the next one.`,
  needsYouTitle: `Jump to the next ${Q} that needs you`,
  threadsLoadFailed: 'AI edit status didn’t load.',

  liveSent: 'Sent. The AI is working on it.',
  liveReplied: (n: number) => `The AI replied on ${Q} ${n}.`,
  liveApplied: 'Applied.',
  liveDiscarded: 'Discarded.',

  conflict: {
    turnInProgress: 'The AI is still working on the last message. Your text is still in the box.',
    threadClosed: 'Someone else just closed this thread.',
    questionDeleted: `This ${Q} was deleted, so there’s nothing to change.`,
  },
  /** A mutation that failed for a reason with no sentence of its own. */
  actionFailed: 'That didn’t go through. Try again.',
} as const;

/** `failure_reason` → the sentence. Unknown / null codes get the generic one. */
export function failureSentence(reason: QuestionEditFailure | null | undefined): string {
  return (reason && QUESTION_EDIT_COPY.failedReason[reason]) || QUESTION_EDIT_COPY.failedReason.unknown;
}
